import { NikonInteractiveDependencyProps } from '../nikonInteractiveConstants';
import { logMessage, logError } from './logging';

// Cache for resource loading promises to prevent duplicate loading
const resourceLoadPromises = {
  scripts: null as Promise<string[]> | null,
};

/**
 * Script loader for JavaScript dependencies
 * Designed to fail silently in production to prevent disruption
 */
export function loadResources(
  dependencies: Set<NikonInteractiveDependencyProps>
): Promise<string[]> {
  try {
    // Check if we already have a promise for script loading
    const existingPromise = resourceLoadPromises.scripts;

    if (existingPromise) {
      return existingPromise;
    }

    const selector = `script[src="{src}"]`;
    
    const loadPromise = Promise.all(
      Array.from(dependencies).map(dependency => {
        try {
          // Check if resource is already in the DOM
          if (document.querySelector(selector.replace('{src}', dependency.src))) {
            logMessage(`${dependency.name} already available.`);
            return Promise.resolve(`${dependency.name} available.`);
          }
          
          return new Promise<string>((resolve) => {
            try {
              const script = document.createElement('script') as HTMLScriptElement;
              script.src = dependency.src;
              script.type = 'text/javascript';
              script.async = true;

              script.onload = () => {
                logMessage(`Loaded ${dependency.name}`);
                resolve(`${dependency.name} loaded.`);
              };

              script.onerror = (e) => {
                logError(`Failed to load ${dependency.name}`, e);
                // Resolve instead of reject to prevent disruption
                resolve(`Failed to load ${dependency.name}, continuing anyway`);
              };

              document.head.appendChild(script);
            } catch (error) {
              logError(`Error creating element for ${dependency.name}`, error);
              // Resolve instead of reject to prevent disruption
              resolve(`Error creating element for ${dependency.name}, continuing anyway`);
            }
          });
        } catch (innerError) {
          logError(`Unexpected error processing dependency ${dependency.name}`, innerError);
          // Return resolved promise to prevent disruption
          return Promise.resolve(`Error processing ${dependency.name}, continuing anyway`);
        }
      })
    ).catch(error => {
      // Catch any errors in the Promise.all and return empty array to prevent disruption
      logError('Error loading resources', error);
      return [] as string[];
    });

    // Store the promise for future reuse
    resourceLoadPromises.scripts = loadPromise;
    
    return loadPromise;
  } catch (error) {
    // Catch any unexpected errors and return empty array to prevent disruption
    logError('Unexpected error in loadResources', error);
    return Promise.resolve([] as string[]);
  }
}

export function clearResourceCache(): void {
  resourceLoadPromises.scripts = null;
}